import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import type { OrganizationBillingContext } from '@fluenceur/types';
import { Button, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwitchToFreeDialog } from './SwitchToFreeDialog';

/**
 * "Switch to the Free plan", under the current paid plan. Only a brand owner
 * or admin may change plans; anyone else reads who can.
 */
export const SwitchToFreeSection = ({
  planKey,
  paidUntil = null,
  accessSource,
  canConfirm = true,
  programs = [],
}: {
  planKey: 'essential' | 'growth';
  /** End of the period already paid; null for a trial, which ends at once. */
  paidUntil?: string | null;
  accessSource?: OrganizationBillingContext['accessSource'];
  canConfirm?: boolean;
  programs?: OrganizationBillingContext['freePlanPrograms'];
}) => {
  const { t } = useTranslation();
  const { canManage } = useIsBrandManager();
  const [isOpen, setIsOpen] = useState(false);
  const planName = t(`pricing.plan.${planKey}.name`);
  const buttonKey =
    accessSource === 'legacy_brand_bypass'
      ? 'settings.subscription.switchToFree.retainedAccess.button'
      : 'settings.subscription.switchToFree.button';

  return (
    <Stack gap={1} align="flex-start">
      <Button
        variant="outline"
        size="sm"
        disabled={!canManage || !canConfirm}
        onClick={() => setIsOpen(true)}
      >
        {t(buttonKey)}
      </Button>
      <Text fontSize="sm" color="fg.muted">
        {canManage
          ? t('settings.subscription.switchToFree.hint')
          : t('settings.subscription.switchToFree.viewerHelper')}
      </Text>
      {isOpen && (
        <SwitchToFreeDialog
          planName={planName}
          paidUntil={paidUntil}
          accessSource={accessSource}
          canConfirm={canManage && canConfirm}
          programs={programs}
          onClose={() => setIsOpen(false)}
        />
      )}
    </Stack>
  );
};
